import makeActionCreator from 'src/lib/makeActionCreator';
import { State } from 'src/redux/reducers/RootReducer';
import {
  deleteRecurringData,
  getRecurringData,
  postRecurringData,
  putRecurringData,
} from 'src/apis/RecurringAPI';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { setAlert } from 'src/redux/actions/alertActions';
import { getContactAction } from 'src/redux/actions/contactActions';
import { setError } from 'src/redux/actions/errorsActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { resetSelections } from 'src/redux/actions/selectionActions';
import { setFormStatus } from 'src/redux/actions/formActions';
import { closeModal } from 'src/redux/actions/modalActions';
import { closeDialog } from 'src/redux/actions/dialogActions';
import { RECURRING_STATE, RecurringPayload } from 'src/models/RecurringModel';
import { MODALS } from 'src/models/ModalModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { DIALOGS } from 'src/models/DialogModel';

export const UPDATE_RECURRING = 'UPDATE_RECURRING';
export const RESET_RECURRING = 'RESET_RECURRING';

export const updateRecurringAction = makeActionCreator(UPDATE_RECURRING);
export const resetRecurringAction = makeActionCreator(RESET_RECURRING);

export const getRecurringAction: (id: number) => ThunkedAction<State> =
  (id: number) => async (dispatch: any, getState: any) => {
    if (!id) {
      return;
    }
    try {
      dispatch(setFormStatus({ [FORM.get_recurring]: FORM_STATUS.loading }));
      const organisation = getState().currentOrganisation.id;
      const response = await getRecurringData(id, organisation);
      if (!response.success) {
        dispatch(setFormStatus({ [FORM.get_recurring]: FORM_STATUS.error }));
        return;
      }
      const { data } = response;
      dispatch(updateRecurringAction({ id: data.id, recurring: data }));
      if (data.contact_id) {
        await dispatch(getContactAction(data.contact_id, FORM.get_recurring));
      }
      dispatch(setFormStatus({ [FORM.get_recurring]: FORM_STATUS.success }));
    } catch (e) { /* Log the error here */
      dispatch(setFormStatus({ [FORM.get_recurring]: FORM_STATUS.error }));
    }
  };

export const postRecurringAction:
(payload: RecurringPayload, close: boolean) => ThunkedAction<State> =
  (payload: RecurringPayload, close) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.add_recurring]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await postRecurringData(organisation, payload);
      const { data, success } = response;
      if (success) {
        dispatch(setFormStatus({ [FORM.add_recurring]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.recurring_saved',
        }));
        dispatch(updateRecurringAction({
          id: data.id,
          recurring: data,
        }));
        if (close) {
          dispatch(closeModal({ modal: MODALS.addRecurring }));
        }
        dispatch(getEntitiesAction(ENTITIES.recurrings));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.add_recurring]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.add_recurring]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: Object.values(response.errors.fields)[0],
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.recurring_saved_error',
        }));
        dispatch(setFormStatus({ [FORM.add_recurring]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setFormStatus({ [FORM.add_recurring]: FORM_STATUS.error }));
    }
  };

export const putRecurringAction:
(id: number, payload: RecurringPayload) => ThunkedAction<State> =
  (id: number, payload: RecurringPayload) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await putRecurringData(id, organisation, payload);
      const { data, success } = response;
      if (success) {
        dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.recurring_updated',
        }));
        dispatch(updateRecurringAction({
          id,
          recurring: data,
        }));
        dispatch(getEntitiesAction(ENTITIES.recurrings));
        dispatch(closeModal({ modal: MODALS.editRecurring }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.put_recurring]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: Object.values(response.errors.fields)[0],
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.recurring_updated_error',
        }));
        dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.recurring_updated_error',
      }));
      dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.error }));
    }
  };

export const deleteRecurringAction:
(id: number) => ThunkedAction<State> =
  (id: number) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.delete_recurring]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await deleteRecurringData(id, organisation);
      if (response.success) {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.recurring_deleted',
        }));
        dispatch(resetRecurringAction({ recurringId: id }));
        dispatch(getEntitiesAction(ENTITIES.recurrings));
        dispatch(closeDialog({ dialog: DIALOGS.deleteRecurring }));
        dispatch(setFormStatus({ [FORM.delete_recurring]: FORM_STATUS.success }));
        dispatch(resetSelections({ entity: ENTITIES.recurrings }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.delete_recurring]: response.message }));
        dispatch(setFormStatus({ [FORM.delete_recurring]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: response.message,
        }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.recurring_deleted_error',
      }));
      dispatch(setFormStatus({ [FORM.delete_recurring]: FORM_STATUS.error }));
    }
  };

export const stopResumeRecurringAction:
(id: number, active: boolean) => ThunkedAction<State> =
  (id: number, active: boolean) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await putRecurringData(id, organisation, {
        state: active ? RECURRING_STATE.active : RECURRING_STATE.stopped,
      });
      if (response.success) {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: active ? 'messages.recurring_resumed' : 'messages.recurring_stopped',
        }));
        dispatch(getEntitiesAction(ENTITIES.recurrings));
        dispatch(closeDialog({ dialog: DIALOGS.stopResumeRecurring }));
        dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.success }));
        dispatch(resetSelections({ entity: ENTITIES.recurrings }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.put_recurring]: response.message }));
        dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: response.message,
        }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.recurring_updated_error',
      }));
      dispatch(setFormStatus({ [FORM.put_recurring]: FORM_STATUS.error }));
    }
  };
